/**
 * The ad-library filter on the Publisher ads section, as pure functions.
 *
 * Two rules the owner asked for, and both are here rather than in the
 * component so they can be pinned by a test:
 *
 *   1. Never offer a library this app has no ads in. An app carrying only
 *      Apple ads shows no filter at all rather than two dead options.
 *   2. The count in the subtitle follows the selection. Pick Google and the
 *      "266 Apple" reading becomes the Google one.
 *
 * `counts` is always the WHOLE-SET count per library, which is what the ads
 * route returns whether or not a filter is applied, so switching libraries
 * never changes the option set under the user.
 */
import { AD_PLATFORMS, type AdPlatform } from './types';

export const PLATFORM_LABEL: Record<AdPlatform, string> = {
  meta: 'Meta',
  google: 'Google',
  apple: 'Apple',
};

/** The libraries this app actually has ads in, in the order they are listed. */
export function availablePlatforms(counts: Record<AdPlatform, number>): AdPlatform[] {
  return AD_PLATFORMS.filter((p) => counts[p] > 0);
}

/**
 * The subtitle's count reading. One library when one is selected, otherwise
 * every library that has something.
 */
export function platformCountsLine(
  counts: Record<AdPlatform, number>,
  selected: AdPlatform | null,
): string {
  const shown = selected ? [selected] : AD_PLATFORMS;
  return shown
    .filter((p) => counts[p] > 0)
    .map((p) => `${counts[p].toLocaleString('en-US')} ${PLATFORM_LABEL[p]}`)
    .join(' · ');
}
